import dayjs from "dayjs";

import { TableSortDirection } from "../../../types/table";

const customIdCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

/**
 * Compares two strings for sorting purposes, case-insensitively.
 *
 * @param a - The first string to compare.
 * @param b - The second string to compare.
 * @param direction - The sort direction, either "ASC" or "DESC".
 * @returns -1 if a should come before b, 1 if a should come after b, or 0 if they are equal.
 */
export function sortString(
  a: string,
  b: string,
  direction: TableSortDirection
) {
  a = a.toLowerCase();
  b = b.toLowerCase();

  if (a < b) {
    if (direction === "DESC") {
      return 1;
    }
    return -1;
  }
  if (a > b) {
    if (direction === "DESC") {
      return -1;
    }
    return 1;
  }
  return 0;
}

/**
 * Compares two numbers for sorting purposes.
 *
 * @param a - The first number to compare.
 * @param b - The second number to compare.
 * @param direction - The sort direction, either "ASC" or "DESC".
 * @returns -1 if a should come before b, 1 if a should come after b, or 0 if they are equal.
 */
export function sortNumber(
  a: number,
  b: number,
  direction: TableSortDirection
) {
  if (a < b) {
    if (direction === "DESC") {
      return 1;
    }
    return -1;
  }
  if (a > b) {
    if (direction === "DESC") {
      return -1;
    }
    return 1;
  }
  return 0;
}

/**
 * Compares two date strings for sorting purposes.
 * Handles undefined values by placing them at the start or end depending on the sort direction.
 *
 * @param a - The first date string to compare (can be undefined).
 * @param b - The second date string to compare (can be undefined).
 * @param direction - The sort direction ("ASC" or "DESC").
 * @returns A negative number if `a` should come before `b`, positive if after, or 0 if equal.
 */
export function sortByDate(
  a: string | undefined,
  b: string | undefined,
  direction: TableSortDirection
) {
  if (!a) {
    if (direction === "DESC") {
      return 1;
    }
    return -1;
  }
  if (!b) {
    if (direction === "DESC") {
      return -1;
    }
    return 1;
  }

  const dateA = dayjs(a);
  const dateB = dayjs(b);

  if (dateA.isSame(dateB, "day")) {
    return 0;
  }

  if (dateA.isAfter(dateB, "day")) {
    if (direction === "DESC") {
      return -1;
    }
    return 1;
  }

  if (direction === "DESC") {
    return 1;
  }
  return -1;
}

/**
 * Sorts two custom ID values using natural ordering so prefixed IDs like
 * `PRJ-2` and `PRJ-10` are ordered by their numeric parts.
 *
 * @param a - The first ID value to compare (string or undefined).
 * @param b - The second ID value to compare (string or undefined).
 * @param direction - The sort direction ("ASC" or "DESC").
 * @returns A negative number if `a` should come before `b`, positive if after, or 0 if equal.
 */
export function sortCustomId(
  a: string | undefined,
  b: string | undefined,
  direction: TableSortDirection
) {
  const comparison = customIdCollator.compare(a ?? "", b ?? "");

  if (direction === "DESC") {
    return comparison * -1;
  }

  return comparison;
}

/**
 * Compares two objects by their custom `id` property first, and if equal (or if either `name` is missing), returns that comparison.
 * If both objects have a `name` and their `id`s are equal, compares by `name` as a secondary sort.
 *
 * @param a - The first object to compare, with optional `id` and `name` properties.
 * @param b - The second object to compare, with optional `id` and `name` properties.
 * @param direction - The sort direction, either "ASC" or "DESC". Defaults to "ASC".
 * @returns A negative number if `a` should come before `b`, positive if after, or 0 if equal.
 */
export function sortByCustomIdAndName(
  a: { id?: string; name?: string },
  b: { id?: string; name?: string },
  direction: TableSortDirection = "ASC"
) {
  const idComparison = sortCustomId(a.id, b.id, direction);

  if (idComparison !== 0 || !a.name || !b.name) {
    return idComparison;
  }

  return sortString(a.name, b.name, direction);
}
